import React from 'react';

import { useAssessmentProgressOptional } from '~/client/components/assessment/assessment-progress-context';
import * as GQL from '~/graphql/generated/client';

export type ConfigStatus =
  | 'unconfigured'
  | 'configured'
  | 'draft'
  | 'draft_by_ai';

interface UseAnswerStatusSyncParams {
  /**
   * Assessment ID for scoping the context key (prevents cross-project conflicts)
   */
  assessmentId: string;

  /**
   * Control identifier for looking up live status from context
   */
  controlId: string;

  /**
   * Answer status from props (derived from Apollo cache/query)
   */
  propAnswerStatus: GQL.AnswerStatus | null | undefined;

  /**
   * Whether the control has content (cards, documents, text, etc.)
   * Used as fallback when answerStatus is null/undefined
   */
  hasContent: boolean;
}

/**
 * Creates a composite key for answer status context storage.
 * cross-project conflicts when the same policy is used in multiple projects.
 *
 * @throws Error if assessmentId or controlIdentifier is empty
 */
export function createAnswerStatusKey(
  assessmentId: string,
  controlIdentifier: string
): string | null {
  // Return null for invalid inputs instead of throwing - allows graceful degradation
  if (!assessmentId || !controlIdentifier) {
    return null;
  }
  return `${assessmentId}:${controlIdentifier}`;
}

interface UseAnswerStatusSyncReturn {
  /**
   * Current configuration status for the dropdown
   */
  configStatus: ConfigStatus;

  /**
   * Whether the control is answered (derived from configStatus).
   * True for 'configured' and 'draft', false for 'unconfigured'.
   * Used for the ConfigureBadge completed prop.
   */
  optimisticAnswered: boolean;

  /**
   * Set the config status (for optimistic updates from user actions).
   */
  setConfigStatus: React.Dispatch<React.SetStateAction<ConfigStatus>>;

  /**
   * The effective answer status (from context if available, else from props)
   */
  effectiveAnswerStatus: GQL.AnswerStatus | null | undefined;
}

/**
 * Custom hook for synchronizing answer status between local state and server.
 *
 * This hook:
 * 1. Uses the AssessmentProgressContext for instant status updates (if available)
 * 2. Falls back to propAnswerStatus when context doesn't have the value
 * 3. Provides optimistic update via setConfigStatus
 * 4. Derives optimisticAnswered from configStatus (single source of truth)
 * 5. Eliminates code duplication across bundle form components
 *
 * The context is updated immediately after mutations (before Apollo cache updates),
 * providing instant feedback like the numAnswers and completionPercent tracking.
 */
export function useAnswerStatusSync({
  assessmentId,
  controlId,
  propAnswerStatus,
  hasContent,
}: UseAnswerStatusSyncParams): UseAnswerStatusSyncReturn {
  const progressContext = useAssessmentProgressOptional();

  // Create composite key scoped to assessment (prevents cross-project conflicts)
  // Returns null if assessmentId or controlId is empty - graceful degradation
  const contextKey = createAnswerStatusKey(assessmentId, controlId);

  // Get live status from context (instant updates) or fall back to props (delayed)
  // If contextKey is null (invalid params), skip context lookup
  const liveAnswerStatus = contextKey
    ? progressContext?.getAnswerStatus(contextKey)
    : undefined;
  const effectiveAnswerStatus =
    liveAnswerStatus !== undefined ? liveAnswerStatus : propAnswerStatus;

  // Compute initial config status based on effective answer status.
  // When answerStatus is explicitly UNCONFIGURED, respect it unconditionally.
  // The hasContent fallback only applies when answerStatus is null/undefined (legacy data).
  const getInitialConfigStatus = (): ConfigStatus => {
    if (effectiveAnswerStatus === GQL.AnswerStatus.Configured) {
      return 'configured';
    }
    if (effectiveAnswerStatus === GQL.AnswerStatus.Draft) {
      return 'draft';
    }
    if (effectiveAnswerStatus === GQL.AnswerStatus.DraftByAi) {
      return 'draft_by_ai';
    }
    if (effectiveAnswerStatus === GQL.AnswerStatus.Unconfigured) {
      return 'unconfigured';
    }
    // answerStatus is null/undefined - fall back to checking content
    return hasContent ? 'configured' : 'unconfigured';
  };

  const [configStatus, setConfigStatus] = React.useState<ConfigStatus>(
    getInitialConfigStatus
  );

  // DERIVED STATE: optimisticAnswered is computed from configStatus
  const optimisticAnswered = configStatus !== 'unconfigured';

  // Track the last CONTEXT status separately from prop status
  const lastLiveStatusRef = React.useRef(liveAnswerStatus);
  const lastPropStatusRef = React.useRef(propAnswerStatus);

  // Sync local state when the effective answer status changes
  // Priority: Context updates (liveAnswerStatus) > Prop updates (propAnswerStatus)
  // Prop updates come from Apollo cache which may be stale during the mutation round-trip.
  React.useEffect(() => {
    const liveStatusChanged = lastLiveStatusRef.current !== liveAnswerStatus;
    const propStatusChanged = lastPropStatusRef.current !== propAnswerStatus;

    // Case 1: Context status changed - ALWAYS sync (highest priority)
    if (liveStatusChanged) {
      lastLiveStatusRef.current = liveAnswerStatus;
      lastPropStatusRef.current = propAnswerStatus;

      if (liveAnswerStatus === GQL.AnswerStatus.Configured) {
        setConfigStatus('configured');
      } else if (liveAnswerStatus === GQL.AnswerStatus.Draft) {
        setConfigStatus('draft');
      } else if (liveAnswerStatus === GQL.AnswerStatus.DraftByAi) {
        setConfigStatus('draft_by_ai');
      } else if (liveAnswerStatus === GQL.AnswerStatus.Unconfigured) {
        setConfigStatus('unconfigured');
      } else if (liveAnswerStatus === null) {
        // null = no answerStatus ever persisted (legacy data, pre-status-tracking)
        setConfigStatus(hasContent ? 'configured' : 'unconfigured');
      } else if (liveAnswerStatus === undefined) {
        // Context was cleared (e.g., rollback to initial state)
        // Fall back to prop status or hasContent
        if (propAnswerStatus === GQL.AnswerStatus.Configured) {
          setConfigStatus('configured');
        } else if (propAnswerStatus === GQL.AnswerStatus.Draft) {
          setConfigStatus('draft');
        } else if (propAnswerStatus === GQL.AnswerStatus.DraftByAi) {
          setConfigStatus('draft_by_ai');
        } else if (propAnswerStatus === GQL.AnswerStatus.Unconfigured) {
          setConfigStatus('unconfigured');
        } else {
          setConfigStatus(hasContent ? 'configured' : 'unconfigured');
        }
      }
      return;
    }

    // Case 2: Prop status changed, but context is undefined (context not tracking this yet)
    if (propStatusChanged && liveAnswerStatus === undefined) {
      lastPropStatusRef.current = propAnswerStatus;

      if (propAnswerStatus === GQL.AnswerStatus.Configured) {
        setConfigStatus('configured');
      } else if (propAnswerStatus === GQL.AnswerStatus.Draft) {
        setConfigStatus('draft');
      } else if (propAnswerStatus === GQL.AnswerStatus.DraftByAi) {
        setConfigStatus('draft_by_ai');
      } else if (propAnswerStatus === GQL.AnswerStatus.Unconfigured) {
        setConfigStatus('unconfigured');
      } else {
        // propAnswerStatus is null/undefined - fall back to checking content
        setConfigStatus(hasContent ? 'configured' : 'unconfigured');
      }
      return;
    }

    // Case 3: hasContent changed (for initial render or content changes)
    if (
      liveAnswerStatus === undefined &&
      (propAnswerStatus === null || propAnswerStatus === undefined)
    ) {
      setConfigStatus(hasContent ? 'configured' : 'unconfigured');
    }
  }, [liveAnswerStatus, propAnswerStatus, hasContent]);

  return {
    configStatus,
    optimisticAnswered,
    setConfigStatus,
    effectiveAnswerStatus,
  };
}

/**
 * Labels for rendering answer status in dropdowns
 */
export const answerStatusLabels: Record<string, string> = {
  unconfigured: 'Unconfigured',
  draft: 'Draft',
  configured: 'Configured',
  draft_by_ai: 'Draft by AI',
  UNCONFIGURED: 'Unconfigured',
  DRAFT: 'Draft',
  CONFIGURED: 'Configured',
  DRAFT_BY_AI: 'Draft by AI',
};

/**
 * Render function for Select dropdown to display human-readable labels
 */
export const renderAnswerStatusValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return 'Select Status';
  }
  return answerStatusLabels[String(value)] ?? 'Select Status';
};
